import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShoppingBag, ShoppingCart, Check, Ruler, Palette } from "lucide-react";
import { toast } from "sonner";
import { ZoomableImage } from "./ZoomableImage";
import { ProductImageCarousel } from "./ProductImageCarousel";
import { ShareButton } from "./ShareButton";
import { ReviewSection } from "./ReviewSection";
import { addToCart } from "@/lib/cart";

const formatINR = (n) =>
  `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STANDARD_SIZES = ["S", "M", "L", "XL", "XXL"];

const buildSizeGrid = (product) => {
  const provided = (Array.isArray(product?.sizes) ? product.sizes : [])
    .map((s) => String(s).trim())
    .filter(Boolean);
  // `stock_by_size` keeps backend stock counts. We hide sizes that are out of
  // stock by disabling them in the grid (same visual as "currently unavailable").
  const stock = product?.stock_by_size || {};
  const stockFor = (label) => {
    // Sizes default to "available" if no stock map exists; if a stock map IS
    // present, only sizes with >0 count are enabled.
    if (Object.keys(stock).length === 0) return Infinity;
    return Number.isFinite(stock[label]) ? Number(stock[label]) : 0;
  };
  if (provided.length === 0) {
    const free = stockFor("Free Size");
    return {
      mode: "free",
      items: [{ label: "Free Size", enabled: free > 0, stock: free }],
    };
  }
  const providedUpper = new Set(provided.map((s) => s.toUpperCase()));
  const standardItems = STANDARD_SIZES.map((s) => {
    const inSizes = providedUpper.has(s);
    const st = stockFor(s);
    return { label: s, enabled: inSizes && st > 0, stock: st };
  });
  const customItems = provided
    .filter((s) => !STANDARD_SIZES.includes(s.toUpperCase()))
    .map((s) => {
      const st = stockFor(s);
      return { label: s, enabled: st > 0, stock: st };
    });
  return { mode: "grid", items: [...standardItems, ...customItems] };
};

// Build the colour-variants list. If admin didn't supply any, fall back to a
// single implicit variant using the product's main image.
const buildColors = (product) => {
  const arr = Array.isArray(product?.colors)
    ? product.colors.filter(Boolean).slice(0, 6)
    : [];
  if (arr.length === 0 && product?.image) {
    return [{ url: product.image, label: "Default", implicit: true }];
  }
  return arr.map((url, idx) => ({
    url,
    label: `Variant ${idx + 1}`,
    implicit: false,
  }));
};

export const ProductDetailDialog = ({
  product,
  open,
  onClose,
  onBuyNow,
  onOpenCart,
}) => {
  const grid = useMemo(() => buildSizeGrid(product), [product]);
  const colors = useMemo(() => buildColors(product), [product]);
  const firstEnabled = useMemo(
    () => grid.items.find((it) => it.enabled)?.label || "Free Size",
    [grid],
  );
  const [selectedSize, setSelectedSize] = useState(firstEnabled);
  const [selectedColorIdx, setSelectedColorIdx] = useState(0);
  const [adding, setAdding] = useState(false);
  // NEW additive state — extra image gallery + text-color picker. These are
  // independent from the existing image-URL `colors` swatches and only become
  // visible when the admin provides the corresponding new fields.
  const [galleryUrl, setGalleryUrl] = useState(null);
  const [selectedColorName, setSelectedColorName] = useState(null);

  // NEW additive: combine the primary image with the additional gallery
  // images (deduped). When length > 1 we show a thumbnail strip.
  const galleryImages = useMemo(() => {
    const list = [product?.image, ...(Array.isArray(product?.images) ? product.images : [])]
      .map((u) => (u ? String(u) : ""))
      .filter(Boolean);
    return Array.from(new Set(list));
  }, [product]);

  const colorNames = useMemo(
    () =>
      Array.isArray(product?.color_names)
        ? product.color_names.filter(Boolean)
        : [],
    [product],
  );

  useEffect(() => {
    setSelectedSize(firstEnabled);
    setSelectedColorIdx(0);
    setGalleryUrl(null);
    setSelectedColorName(null);
  }, [product?.id, firstEnabled]);

  // Switching the existing image-URL colour-variant resets any manual gallery
  // override so the variant image takes over (matches prior behaviour).
  useEffect(() => {
    setGalleryUrl(null);
  }, [selectedColorIdx]);

  // Preload all colour-variant images so swatch clicks switch instantly.
  useEffect(() => {
    if (!open) return;
    const preloaded = colors.map((c) => {
      const img = new Image();
      img.src = c.url;
      return img;
    });
    return () => {
      preloaded.forEach((img) => {
        img.src = "";
      });
    };
  }, [open, colors]);

  // Preload extra gallery images for instant swap on thumb tap.
  useEffect(() => {
    if (!open) return;
    const preloaded = galleryImages.map((u) => {
      const img = new Image();
      img.src = u;
      return img;
    });
    return () => {
      preloaded.forEach((img) => {
        img.src = "";
      });
    };
  }, [open, galleryImages]);

  const activeColor = colors[selectedColorIdx] || colors[0];
  const showSwatches = colors.length > 1 || (colors.length === 1 && !colors[0].implicit);
  // Gallery thumb override takes priority over variant image; everything else
  // (including the original variant→main fallback) stays identical.
  const mainImage = galleryUrl || activeColor?.url || product?.image;

  const handleAdd = () => {
    if (!product) return;
    // If the customer picked a text-based colour, use it as the human label;
    // otherwise fall back to the existing image-variant label (unchanged).
    const finalColorLabel =
      selectedColorName || (showSwatches ? activeColor?.label : null);
    addToCart(
      product,
      {
        size: selectedSize,
        color: showSwatches ? activeColor?.url : null,
        colorLabel: finalColorLabel,
      },
      1,
    );
    setAdding(true);
    toast.success("Added to your cart", {
      description: `${product.title} · Size ${selectedSize}${finalColorLabel ? " · " + finalColorLabel : ""}`,
      duration: 2000,
      action: {
        label: "View cart",
        onClick: () => onOpenCart?.(),
      },
    });
    setTimeout(() => setAdding(false), 1200);
  };

  const handleBuy = () => {
    if (!product) return;
    const finalColorLabel =
      selectedColorName || (showSwatches ? activeColor?.label : null);
    onBuyNow?.({
      ...product,
      selectedSize,
      selectedColor: showSwatches ? activeColor?.url : null,
      selectedColorLabel: finalColorLabel,
    });
  };

  return (
    <AnimatePresence>
      {open && product && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center p-0 sm:p-6 bg-black/45 backdrop-blur-sm"
          onClick={onClose}
          data-testid="product-detail-modal"
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:max-w-4xl bg-white/95 backdrop-blur-2xl border border-white/60 sm:rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.22)] overflow-hidden flex flex-col sm:flex-row max-h-screen sm:max-h-[88vh]"
          >
            <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
              <ShareButton
                product={product}
                testId="detail-share-btn"
                className="bg-white/85 hover:bg-white text-[#0B0C10] px-3 py-2"
              />
              <button
                onClick={onClose}
                data-testid="detail-close-btn"
                aria-label="close"
                className="w-9 h-9 rounded-full bg-white/85 hover:bg-white flex items-center justify-center transition-colors"
              >
                <X size={16} strokeWidth={1.8} />
              </button>
            </div>

            <div className="relative sm:w-3/5 bg-white/40 p-4 sm:p-6 pt-14 sm:pt-6 flex flex-col items-center gap-3">
              <div className="relative w-full aspect-square sm:aspect-[4/5]">
                {galleryImages.length > 1 ? (
                  <ProductImageCarousel
                    images={galleryImages}
                    activeIndex={Math.max(0, galleryImages.indexOf(mainImage))}
                    onIndexChange={(idx) => setGalleryUrl(galleryImages[idx])}
                    alt={product.title}
                  />
                ) : (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={mainImage}
                      initial={{ opacity: 0, scale: 1.02 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.28, ease: "easeOut" }}
                      className="absolute inset-0"
                    >
                      <ZoomableImage src={mainImage} alt={product.title} />
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>

              {/* NEW additive: scrollable thumbnail strip when the admin has
                  attached extra product images. Works on mobile (swipe) and
                  desktop (click). Hidden when only the primary image exists. */}
              {galleryImages.length > 1 && (
                <div
                  data-testid="detail-gallery-strip"
                  className="w-full overflow-x-auto"
                  style={{ scrollbarWidth: "none" }}
                >
                  <div className="flex gap-2 pb-1 snap-x snap-mandatory">
                    {galleryImages.map((url, idx) => {
                      const active = url === mainImage;
                      return (
                        <button
                          key={`${url}-${idx}`}
                          type="button"
                          onClick={() => setGalleryUrl(url)}
                          data-testid={`detail-gallery-thumb-${idx}`}
                          aria-pressed={active}
                          aria-label={`view image ${idx + 1}`}
                          className={`shrink-0 snap-start w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden transition-all ${
                            active
                              ? "ring-2 ring-[#E0B53C] shadow-[0_6px_18px_rgba(224,181,60,0.4)]"
                              : "ring-1 ring-black/10 opacity-75 hover:opacity-100"
                          }`}
                        >
                          <img
                            src={url}
                            alt={`${product.title} – ${idx + 1}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="sm:w-2/5 flex flex-col p-5 sm:p-7 gap-5 overflow-y-auto pb-24 sm:pb-7">
              <div className="space-y-2">
                <p className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
                  {product.category}
                </p>
                <h2 className="font-display text-2xl sm:text-3xl text-[#0B0C10] leading-tight">
                  {product.title}
                </h2>
                <p className="font-display text-2xl sm:text-3xl font-medium text-[#0B0C10]">
                  {formatINR(product.price)}
                  {product.original_price > product.price && (
                    <span className="ml-2 text-base sm:text-lg text-neutral-500 line-through font-normal">{formatINR(product.original_price)}</span>
                  )}
                </p>
              </div>

              {product.description && (
                <p className="text-sm text-neutral-700 leading-relaxed">
                  {product.description}
                </p>
              )}

              {/* Colour swatches — round circles previewing each variant.
                  Sits right above the Size grid as specified. */}
              {showSwatches && (
                <div className="space-y-3" data-testid="detail-color-picker">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Palette
                        size={14}
                        strokeWidth={1.8}
                        className="text-[#064E3B]"
                      />
                      <span className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
                        Colour · {colors.length}
                      </span>
                    </div>
                    <span
                      className="text-sm font-medium text-[#0B0C10]"
                      data-testid="detail-selected-color"
                    >
                      {activeColor.label}
                    </span>
                  </div>
                  <div
                    className="flex flex-wrap gap-3"
                    data-testid="detail-color-swatches"
                  >
                    {colors.map((c, idx) => {
                      const active = idx === selectedColorIdx;
                      return (
                        <motion.button
                          key={idx}
                          type="button"
                          onClick={() => setSelectedColorIdx(idx)}
                          whileTap={{ scale: 0.92 }}
                          animate={{ scale: active ? 1.1 : 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 22,
                          }}
                          data-testid={`detail-color-${idx}`}
                          aria-pressed={active}
                          aria-label={c.label}
                          className={`relative w-11 h-11 rounded-full overflow-hidden ring-offset-2 ring-offset-white transition-shadow ${
                            active
                              ? "ring-2 ring-[#E0B53C] shadow-[0_6px_18px_rgba(224,181,60,0.45)]"
                              : "ring-1 ring-black/10 hover:ring-[#0B0C10]/40"
                          }`}
                        >
                          <img
                            src={c.url}
                            alt={c.label}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover"
                          />
                          {active && (
                            <span
                              aria-hidden="true"
                              className="absolute inset-0 flex items-center justify-center bg-black/15"
                            >
                              <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center shadow">
                                <Check
                                  size={12}
                                  strokeWidth={3}
                                  className="text-[#0B0C10]"
                                />
                              </span>
                            </span>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* NEW additive: Available Colors (text). Independent from the
                  existing image-URL swatches — only renders when the admin has
                  populated `color_names`. Pure text chips, single-select. */}
              {colorNames.length > 0 && (
                <div className="space-y-3" data-testid="detail-color-names">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Palette
                        size={14}
                        strokeWidth={1.8}
                        className="text-[#064E3B]"
                      />
                      <span className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
                        Available Colors · {colorNames.length}
                      </span>
                    </div>
                    {selectedColorName && (
                      <span
                        className="text-sm font-medium text-[#0B0C10]"
                        data-testid="detail-selected-color-name"
                      >
                        {selectedColorName}
                      </span>
                    )}
                  </div>
                  <div
                    className="flex flex-wrap gap-2"
                    data-testid="detail-color-name-chips"
                  >
                    {colorNames.map((name, idx) => {
                      const active = name === selectedColorName;
                      return (
                        <button
                          key={`${name}-${idx}`}
                          type="button"
                          onClick={() =>
                            setSelectedColorName(active ? null : name)
                          }
                          data-testid={`detail-color-name-${idx}`}
                          aria-pressed={active}
                          className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium border transition-all ${
                            active
                              ? "bg-[#0B0C10] text-white border-[#0B0C10] shadow-[0_4px_12px_rgba(11,12,16,0.25)]"
                              : "bg-white text-[#0B0C10] border-black/15 hover:border-[#0B0C10]"
                          }`}
                        >
                          {active && (
                            <Check size={12} strokeWidth={2.5} />
                          )}
                          {name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Size picker — explicit 5-column CSS grid for the standard
                  S/M/L/XL/XXL sizes. Custom non-standard sizes (e.g. numeric
                  jeans sizes) render in a separate flex row below. */}
              <div className="space-y-3" data-testid="detail-size-picker">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Ruler
                      size={14}
                      strokeWidth={1.8}
                      className="text-[#064E3B]"
                    />
                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold">
                      Size
                    </span>
                  </div>
                  <span
                    className="text-sm font-medium text-[#0B0C10]"
                    data-testid="detail-selected-size"
                  >
                    {selectedSize}
                  </span>
                </div>

                {grid.mode === "free" ? (
                  <div
                    data-testid="detail-size-grid"
                    style={{ display: "flex", gap: "12px", marginTop: "8px" }}
                  >
                    <SizeBox
                      label="Free Size"
                      enabled
                      active={selectedSize === "Free Size"}
                      onClick={() => setSelectedSize("Free Size")}
                      wide
                    />
                  </div>
                ) : (
                  <>
                    <div
                      data-testid="detail-size-grid"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
                        gap: "12px",
                        maxWidth: "320px",
                        marginTop: "8px",
                      }}
                    >
                      {STANDARD_SIZES.map((label) => {
                        const item = grid.items.find(
                          (i) => i.label === label,
                        ) || { enabled: false };
                        const enabled = item.enabled;
                        const active = enabled && label === selectedSize;
                        return (
                          <SizeBox
                            key={label}
                            label={label}
                            enabled={enabled}
                            active={active}
                            onClick={() => enabled && setSelectedSize(label)}
                          />
                        );
                      })}
                    </div>

                    {/* Custom (non-standard) sizes — rendered separately. */}
                    {grid.items.some(
                      (it) => !STANDARD_SIZES.includes(it.label.toUpperCase()),
                    ) && (
                      <div
                        data-testid="detail-custom-sizes"
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "12px",
                          marginTop: "8px",
                        }}
                      >
                        {grid.items
                          .filter(
                            (it) =>
                              !STANDARD_SIZES.includes(it.label.toUpperCase()),
                          )
                          .map((item) => {
                            const active = item.label === selectedSize;
                            return (
                              <SizeBox
                                key={item.label}
                                label={item.label}
                                enabled={item.enabled}
                                active={active}
                                onClick={() =>
                                  item.enabled && setSelectedSize(item.label)
                                }
                              />
                            );
                          })}
                      </div>
                    )}

                    <p className="text-[11px] text-neutral-500">
                      Greyed-out sizes are currently unavailable.
                    </p>
                  </>
                )}
              </div>

              <div className="mt-auto space-y-3 hidden sm:block">
                <div className="flex gap-3">
                  <button
                    onClick={handleAdd}
                    data-testid="detail-add-to-cart-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-[#0B0C10] text-[#0B0C10] rounded-full py-3 text-sm font-medium hover:bg-[#0B0C10] hover:text-white transition-all"
                  >
                    {adding ? (
                      <Check size={16} strokeWidth={2} />
                    ) : (
                      <ShoppingCart size={16} strokeWidth={1.8} />
                    )}
                    {adding ? "Added" : "Add to Cart"}
                  </button>
                  <button
                    onClick={handleBuy}
                    data-testid="detail-buy-now-btn"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0B0C10] text-white rounded-full py-3 text-sm font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all"
                  >
                    <ShoppingBag size={16} strokeWidth={1.8} />
                    Buy Now
                  </button>
                </div>
                <p className="text-[11px] text-center text-neutral-500 uppercase tracking-[0.18em]">
                  Cash on Delivery · Free shipping ₹999+
                </p>
              </div>

              {/* Reviews & Ratings — isolated, additive feature. Reads/writes
                  its own `mb_reviews_v1` localStorage key only. */}
              <ReviewSection productId={product.id} />
            </div>

            <div
              data-testid="detail-mobile-actionbar"
              className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-2xl border-t border-black/5 px-4 py-3 flex gap-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
            >
              <button
                onClick={handleAdd}
                data-testid="detail-add-to-cart-btn-mobile"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-[#0B0C10] text-[#0B0C10] rounded-full py-3 text-sm font-medium active:scale-[0.98] transition-all"
              >
                {adding ? (
                  <Check size={16} strokeWidth={2} />
                ) : (
                  <ShoppingCart size={16} strokeWidth={1.8} />
                )}
                {adding ? "Added" : "Add"}
              </button>
              <button
                onClick={handleBuy}
                data-testid="detail-buy-now-btn-mobile"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#0B0C10] text-white rounded-full py-3 text-sm font-medium active:scale-[0.98] transition-all"
              >
                <ShoppingBag size={16} strokeWidth={1.8} />
                Buy Now
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};


// One size cell in the grid. Standalone 45×45 square, white bg, solid border,
// centered text. Disabled = red strikethrough + diagonal cross line + X badge,
// fully unclickable.
const SizeBox = ({ label, enabled, active, onClick, wide = false }) => {
  const disabled = !enabled;
  const slug = label.replace(/\s+/g, "-");
  const baseStyle = {
    width: wide ? "auto" : "45px",
    minWidth: wide ? "100px" : "45px",
    height: "45px",
    padding: wide ? "0 14px" : 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "10px",
    borderStyle: "solid",
    borderWidth: "2px",
    backgroundColor: "#FFFFFF",
    fontWeight: 700,
    fontSize: "13px",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    cursor: disabled ? "not-allowed" : "pointer",
    position: "relative",
    transition:
      "border-color 200ms ease, box-shadow 200ms ease, transform 160ms ease",
    userSelect: "none",
  };
  const stateStyle = disabled
    ? {
        borderColor: "rgba(248, 113, 113, 0.55)",
        color: "rgba(220, 38, 38, 0.85)",
      }
    : active
      ? {
          borderColor: "#E0B53C",
          color: "#0B0C10",
          boxShadow: "0 6px 18px rgba(224,181,60,0.25)",
          transform: "scale(1.06)",
        }
      : {
          borderColor: "rgba(0,0,0,0.12)",
          color: "#1F1F1F",
        };

  return (
    <button
      type="button"
      disabled={disabled}
      aria-disabled={disabled}
      aria-pressed={active}
      onClick={onClick}
      data-testid={
        disabled ? `detail-size-${slug}-disabled` : `detail-size-${slug}`
      }
      style={{ ...baseStyle, ...stateStyle }}
    >
      <span
        style={{
          position: "relative",
          zIndex: 1,
          textDecoration: disabled ? "line-through" : "none",
          textDecorationColor: "rgba(220,38,38,0.85)",
          textDecorationThickness: "2px",
        }}
      >
        {label}
      </span>

      {disabled && (
        <>
          {/* Diagonal red strike across the box */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <span
              style={{
                display: "block",
                width: "140%",
                height: "2px",
                background: "rgba(220, 38, 38, 0.75)",
                borderRadius: "9999px",
                transform: "rotate(-28deg)",
              }}
            />
          </span>
          {/* X corner badge — the disabled-wrong overlay */}
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "-6px",
              right: "-6px",
              width: "18px",
              height: "18px",
              borderRadius: "9999px",
              backgroundColor: "#EF4444",
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
              border: "2px solid #FFFFFF",
            }}
          >
            <X size={10} strokeWidth={3} />
          </span>
        </>
      )}
    </button>
  );
};
